import React, { useState, ChangeEvent } from 'react';
import { Container, Grid, Button, Typography } from '@mui/material';
import Page from '../../../components/Page';
import { useNavigate } from 'react-router-dom';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { TextField } from 'src/components';
import { useFormik } from 'formik';
import { teacherValidationSchema } from 'src/validations/admin';
import '../../styles/addstudent.css';
import palette from 'src/theme/palette';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { teacherService } from 'src/services/teacher.service';
import { studentService } from 'src/services/student.service';
import { toast } from 'react-toastify';

interface TeacherFormValues {
  firstName: string;
  lastName: string;
  email: string;
  teacherId: string;
  extendedproperties: {
    teacherId: string;
    [key: string]: any;
  };
  password: string;
  confirmPassword: string;
}

export default function AddTeacher() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [fileError, setFileError] = useState<string>('');
  const [photoUrl, setPhotoUrl] = useState<string>(
    'https://i.pinimg.com/736x/48/42/6e/48426e7f19dd4e44f8071ad330e0148c.jpg'
  );
  const [profilePic, setProfilePic] = useState<File | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const mutation = useMutation({
    mutationFn: async (values: TeacherFormValues) => {
      const teacher = await teacherService.createTeacher({
        firstname: values.firstName,
        lastname: values.lastName,
        email: values.email,
        password: values.password,
        extendedproperties: {
          teacherId: values.teacherId,
        },
      });

      if (profilePic) {
        await studentService.updateProfilePicture(teacher.user.id, profilePic);
      }

      return teacher;
    },
    onSuccess: () => {
      toast.success('Teacher created successfully');
      queryClient.invalidateQueries({ queryKey: ['teachers'] });

      navigate('/admin/teachers');
    },
    onError: (error: Error) => {
      console.error('Error creating teacher:', error);
      toast.error(error.message || 'Failed to create teacher');
    },
  });

  const formik = useFormik<TeacherFormValues>({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      teacherId: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema: teacherValidationSchema,
    onSubmit: async (values: TeacherFormValues) => {
      try {
        await mutation.mutateAsync(values);
      } catch (error) {
        console.error('Error creating teacher:', error);
      }
    },
  });

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (file) {
      const fileType = file.type;
      if (!['image/jpeg', 'image/png', 'image/jpg'].includes(fileType)) {
        setFileError('Only .JPG, .JPEG, or .PNG files are allowed.');
        return;
      }

      try {
        setLoading(true);
        const reader = new FileReader();
        reader.onloadend = () => {
          setPhotoUrl(reader.result as string);
        };
        reader.readAsDataURL(file);
        setProfilePic(file);
        setFileError('');
      } catch (error) {
        console.error('Error handling photo:', error);
        setFileError('Failed to process photo. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeletePhoto = async () => {
    setPhotoUrl('https://i.pinimg.com/736x/48/42/6e/48426e7f19dd4e44f8071ad330e0148c.jpg');
    setProfilePic(null);
  };

  return (
    <Page title="Add New Teacher">
      <Container className="_container" maxWidth={false}>
        <Typography variant="h4">Add New Teacher</Typography>
        <div className="_divider"></div>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={2} md={1} lg={1}>
            <img
              src={photoUrl}
              alt="Teacher profile"
              style={{ borderRadius: 100, height: 88, width: 88, objectFit: 'cover' }}
            />
          </Grid>
          <Grid item xs={12} sm={10} md={10} lg={10}>
            <Typography variant="subtitle1" sx={{ color: 'grey.600' }}>
              only support .JPG, .JPEG, or .PNG file.
            </Typography>

            <div className="upload_row" style={{ marginTop: 0 }}>
              <input type="file" id="_upload_pic" onChange={handleFileChange} accept="image/jpeg,image/png,image/jpg" />
              <label className="_btn" htmlFor="_upload_pic">
                {loading ? ' Uploading...' : '  Upload New photo'}
              </label>
              <button
                type="button"
                className="_btn"
                style={{ backgroundColor: 'var(--white)', color: 'var(--primary-main)' }}
                onClick={handleDeletePhoto}
              >
                Delete Photo
              </button>
            </div>
            {fileError && (
              <Typography variant="caption" color="error" sx={{ fontWeight: 700, mt: 1 }}>
                {fileError}
              </Typography>
            )}
          </Grid>
        </Grid>
        <br />

        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth={true}
                label="First Name"
                placeholder="Enter First Name"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                name="firstName"
                error={Boolean(formik.touched.firstName && formik.errors.firstName)}
                helperText={formik.touched.firstName && formik.errors.firstName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                placeholder="Enter Last Name"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                name="lastName"
                error={Boolean(formik.touched.lastName && formik.errors.lastName)}
                helperText={formik.touched.lastName && formik.errors.lastName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                placeholder="Enter Email"
                value={formik.values.email}
                onChange={formik.handleChange}
                name="email"
                error={Boolean(formik.touched.email && formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Teacher ID"
                placeholder="Enter Teacher ID"
                value={formik.values.teacherId}
                onChange={formik.handleChange}
                name="teacherId"
                error={Boolean(formik.touched.teacherId && formik.errors.teacherId)}
                helperText={formik.touched.teacherId && formik.errors.teacherId}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Password"
                placeholder="Enter Password"
                type={showPassword ? 'text' : 'password'}
                value={formik.values.password}
                onChange={formik.handleChange}
                name="password"
                error={Boolean(formik.touched.password && formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                right={
                  <span onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? (
                      <FiEyeOff size={18} color={palette.grey[500]} />
                    ) : (
                      <FiEye size={18} color={palette.grey[500]} />
                    )}
                  </span>
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Confirm Password"
                placeholder="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                name="confirmPassword"
                error={Boolean(formik.touched.confirmPassword && formik.errors.confirmPassword)}
                helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                right={
                  <span onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                    {showConfirmPassword ? (
                      <FiEyeOff size={18} color={palette.grey[500]} />
                    ) : (
                      <FiEye size={18} color={palette.grey[500]} />
                    )}
                  </span>
                }
              />
            </Grid>
          </Grid>
          <div className="_add_st_footer_row" style={{ marginTop: 20 }}>
            <Button
              variant="outlined"
              sx={{
                height: 48,
                color: 'primary.light',
                width: 120,
                m: 0.5,
              }}
              onClick={() => navigate(-1)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ height: 48, width: 120, m: 0.5 }}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </form>
      </Container>
    </Page>
  );
}
